import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardShell } from "@/components/DashboardShell";
import { ADMIN_NAV } from "@/lib/nav";
import { supabase } from "@/integrations/supabase/client";
import {
  SERVICE_LABEL,
  SERVICE_STATUS_META,
  SERVICE_TYPES,
  type PartnerServiceProfile,
  type ServiceStatus,
  type ServiceType,
} from "@/lib/services";

export const Route = createFileRoute("/_authenticated/admin/service-requests")({
  head: () => ({
    meta: [
      { title: "Service activation requests · Landoo Admin" },
      {
        name: "description",
        content: "Review, approve, reject or suspend each partner service independently.",
      },
    ],
  }),
  component: ServiceRequestsPage,
});

function ServiceRequestsPage() {
  const [filter, setFilter] = useState<ServiceType | "all">("all");
  const { data: rows = [], refetch } = useQuery({
    queryKey: ["admin", "service-requests"],
    queryFn: async () => {
      const { data } = await supabase
        .from("partner_service_profiles")
        .select("*")
        .order("created_at", { ascending: false });
      return (data as unknown as PartnerServiceProfile[]) ?? [];
    },
  });

  const visible = filter === "all" ? rows : rows.filter((r) => r.service_type === filter);

  const act = async (row: PartnerServiceProfile, status: ServiceStatus, note?: string) => {
    const patch: {
      service_status: ServiceStatus;
      approval_status: string;
      admin_notes: string | null;
      activated_at?: string;
      expires_at?: string;
      verification_status?: string;
      subscription_status?: string;
    } = {
      service_status: status,
      approval_status:
        status === "active" ? "approved" : status === "rejected" ? "rejected" : "pending",
      admin_notes: note ?? null,
    };
    if (status === "active") {
      patch.activated_at = new Date().toISOString();
      patch.expires_at = new Date(Date.now() + 365 * 864e5).toISOString();
      patch.verification_status = "verified";
      patch.subscription_status = "active";
    }
    const { error } = await supabase
      .from("partner_service_profiles")
      .update(patch)
      .eq("id", row.id);
    if (error) return toast.error(error.message);
    toast.success(
      `${SERVICE_LABEL[row.service_type]} service ${status.replace(/_/g, " ")} — other services unchanged`,
    );
    void refetch();
  };

  return (
    <DashboardShell title="Service activation requests" role="Admin" items={ADMIN_NAV}>
      <div className="flex flex-wrap gap-1.5">
        <Chip
          active={filter === "all"}
          onClick={() => setFilter("all")}
          label={`All · ${rows.length}`}
        />
        {SERVICE_TYPES.map((t) => (
          <Chip
            key={t}
            active={filter === t}
            onClick={() => setFilter(t)}
            label={`${SERVICE_LABEL[t]} · ${rows.filter((r) => r.service_type === t).length}`}
          />
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Each service is reviewed on its own. Approving or suspending one service never changes the
        partner's other services.
      </p>

      <div className="mt-4 grid gap-2">
        {visible.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
            No service requests.
          </div>
        )}
        {visible.map((r) => (
          <div key={r.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-bold text-brown-dark">
                  {SERVICE_LABEL[r.service_type]} · {r.business_name ?? "Partner"}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {r.partner_role ?? "—"} · {r.is_primary ? "Primary service" : "Add-on service"} ·
                  partner {r.partner_id.slice(0, 8)}
                </div>
              </div>
              <span
                className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${SERVICE_STATUS_META[r.service_status].cls}`}
              >
                {SERVICE_STATUS_META[r.service_status].label}
              </span>
            </div>
            <div className="mt-2 grid gap-1 text-[11px] text-muted-foreground sm:grid-cols-4">
              <span>Verification: {r.verification_status}</span>
              <span>Agreement: {r.agreement_status}</span>
              <span>Subscription: {r.subscription_status}</span>
              <span>Approval: {r.approval_status}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Btn onClick={() => act(r, "active")} className="bg-emerald-600 text-white">
                Approve
              </Btn>
              <Btn
                onClick={() => act(r, "rejected", "Rejected by admin")}
                className="bg-destructive text-white"
              >
                Reject
              </Btn>
              <Btn
                onClick={() => act(r, "registration_draft", "Changes requested by admin")}
                className="bg-brand text-brown-dark"
              >
                Request changes
              </Btn>
              <Btn
                onClick={() => act(r, "suspended", "Service suspended by admin")}
                className="border border-border bg-background text-brown-dark"
              >
                Suspend service
              </Btn>
            </div>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}

function Chip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-xs font-semibold ${active ? "bg-brown text-primary-foreground" : "bg-secondary text-brown-dark"}`}
    >
      {label}
    </button>
  );
}

function Btn({
  onClick,
  className,
  children,
}: {
  onClick: () => void;
  className: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-[11px] font-semibold shadow-soft ${className}`}
    >
      {children}
    </button>
  );
}
